import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import React from 'react';
import { EntryItem } from './patient-timeline-entries';
import { cmxDateTime, objectUtils } from '@codametrix/ui-common';
/**
 * Enum representing different author types.
 * @enum {string}
 */ var AuthorType = /*#__PURE__*/ function(AuthorType) {
    AuthorType["ADMITTING"] = "admitting";
    AuthorType["ATTENDING"] = "attending";
    AuthorType["AUTHORING"] = "authoring";
    AuthorType["BILLING"] = "billing";
    AuthorType["ENTERING"] = "entering";
    AuthorType["MEDICATION_ORDERING"] = "medicationOrdering";
    AuthorType["ORDERING"] = "ordering";
    AuthorType["PRIMARY_AUTHENTICATOR"] = "primaryAuthenticator";
    AuthorType["SCHEDULING"] = "scheduling";
    AuthorType["VERIFYING"] = "verifying";
    AuthorType["FINAL_SIGN_PROVIDER"] = "finalSignProvider";
    AuthorType["PRIMARY_SURGEON"] = "primarySurgeon";
    AuthorType["COSIGNING"] = "cosigning";
    return AuthorType;
}(AuthorType || {});
/**
 * Labels for different provider types.
 * @type {Object.<string, string>}
 */ const providerLabels = {
    admitting: 'Admitting provider',
    attending: 'Attending provider',
    authoring: 'Note author',
    billing: 'Billing provider',
    entering: 'Entered by',
    medicationOrdering: 'Ordering provider',
    ordering: 'Ordering provider',
    verifying: 'Verified by',
    scheduling: 'Scheduled',
    finalSignProvider: 'Final sign provider',
    primaryAuthenticator: 'Primary authenticator',
    primarySurgeon: 'Primary surgeon',
    cosigning: 'Cosigning provider'
};
/**
 * Filters providers based on a filter function.
 *
 * @param {ProviderFilterFunction[]} fns - The filter functions.
 * @param {CMxAPI.ProviderInfo[]} providers - List of providers.
 * @returns {CMxAPI.ProviderInfo[]} - Filtered list of providers.
 */ const providerFilter = (fns, providers)=>{
    if (!providers) {
        return [];
    }
    fns.forEach((fn)=>{
        providers = providers.filter(fn);
    });
    return providers;
};
/**
 * Creates a simple filter function based on author type.
 *
 * @param {AuthorType} authorType - The author type to filter.
 * @returns {ProviderFilterFunction} - The filter function.
 */ const simpleFilter = (authorType)=>{
    return (provider)=>provider.type === authorType;
};
/**
 * Creates a filter function to check if primary
 *
 * @returns {ProviderFilterFunction} - The filter function.
 */ const primaryFilter = ()=>{
    return (provider)=>!!provider?.is_primary;
};
/**
 * Generates a display name for a provider.
 *
 * @param {CMxAPI.ProviderInfo} provider - The provider information.
 * @returns {string} - The formatted provider name.
 */ const displayProviderName = (provider)=>{
    if (!provider.given_name && !provider.family_name) {
        return 'N/A';
    }
    return `${provider.family_name}, ${provider.given_name} ${provider?.type === "medicationOrdering" ? ` ${getProviderSpecialties(provider?.provider_specialty)}` : ''}
  `;
};
/**
 * Generates a display name for a billing provider.
 *
 * @param {CMxAPI.ProviderInfo} provider - The provider information.
 * @returns {string} - The formatted provider name.
 */ const displayBillingProviderName = (provider)=>{
    if (!provider.given_name && !provider.family_name) {
        return 'N/A';
    }
    return `${provider.family_name}, ${provider.given_name} ${getProviderSpecialties(provider?.provider_specialty)}`;
};
/**
 * Displays a list of providers with labels.
 *
 * @param {CMxAPI.ProviderInfo[]} providers - List of providers.
 * @param {string} label - The label for the provider type.
 * @returns {JSX.Element[]} - Array of React elements representing the display.
 */ const displayProviders = (providers, label)=>{
    const billingProviders = providers.filter((provider)=>provider.type === "billing");
    const finalSignerProviders = providers.filter((provider)=>provider.type === "finalSignProvider");
    let otherProviders = providers.filter((provider)=>provider.type !== "billing" && provider.type !== "finalSignProvider");
    // billing provider for DFT
    const billingProvidersGrouped = objectUtils.groupBy(billingProviders, 'identifier');
    const billingProvidersReduced = [];
    Object.keys(billingProvidersGrouped).forEach((identifier)=>{
        billingProvidersReduced.push(billingProvidersGrouped[identifier].reduce((reducedProvider, currentProvider)=>{
            if (!reducedProvider.identifier) {
                return currentProvider;
            }
            return {
                ...reducedProvider,
                provider_specialty: `${reducedProvider.provider_specialty}, ${currentProvider.provider_specialty}`
            };
        }, {
            identifier: '',
            given_name: '',
            family_name: '',
            provider_specialty: '',
            type: 'billing'
        }));
    });
    // final sign provider keeping the provider with the last date
    const finalSignProvidersWithDate = finalSignerProviders.filter((provider)=>provider?.custom_properties?.datetime).sort((provA, provB)=>cmxDateTime.sortByDate(provA?.custom_properties.datetime, provB?.custom_properties.datetime));
    const newestFinalSignProvidersWithDate = finalSignProvidersWithDate.length > 0 ? [
        finalSignProvidersWithDate[0]
    ] : [];
    const finalSignProvidersWithNoDate = finalSignerProviders.filter((provider)=>!provider?.custom_properties?.datetime);
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: [
            ...otherProviders,
            ...billingProvidersReduced,
            ...newestFinalSignProvidersWithDate,
            ...finalSignProvidersWithNoDate
        ].map((provider)=>/*#__PURE__*/ _jsx(EntryItem, {
                field: label + ':',
                value: provider?.type === "billing" ? displayBillingProviderName(provider) : displayProviderName(provider)
            }, `provider-${provider.identifier}`))
    });
};
/**
 * Creates a provider filter based on author type.
 *
 * @param {AuthorType} authorType - The author type to filter.
 * @param {AuthorType} labelType - The label of type to filter. optional
 * @returns {function(CMxAPI.ProviderInfo[]): JSX.Element[]} - The provider filter function.
 */ const createProviderFilter = (authorType, labelType)=>{
    return (providers)=>{
        const filtersArray = [];
        filtersArray.push(simpleFilter(authorType));
        if (labelType === "primarySurgeon") {
            filtersArray.push(primaryFilter());
        }
        const providerLabel = providerLabels[labelType ?? authorType];
        return displayProviders(providerFilter(filtersArray, providers), providerLabel);
    };
};
const getProviderSpecialties = (specialties)=>{
    const specialtiesArray = Array.isArray(specialties) ? specialties : [
        specialties
    ];
    return specialties ? `(${specialtiesArray?.join(', ')})` : '';
};
/**
 * Object containing provider filters for each provider type.
 * @type {Object.<ProviderType, function(CMxAPI.ProviderInfo[]): JSX.Element[]>}
 */ const ProviderFilters = {
    admitting: createProviderFilter("admitting"),
    attending: createProviderFilter("attending"),
    billing: createProviderFilter("billing"),
    authoring: createProviderFilter("authoring"),
    entering: createProviderFilter("entering"),
    medicationOrdering: createProviderFilter("medicationOrdering"),
    ordering: createProviderFilter("ordering"),
    primaryAuthenticator: createProviderFilter("primaryAuthenticator"),
    scheduling: createProviderFilter("scheduling"),
    verifying: createProviderFilter("verifying"),
    finalSignProvider: createProviderFilter("finalSignProvider"),
    primarySurgeon: createProviderFilter("scheduling", "primarySurgeon"),
    cosigning: createProviderFilter("cosigning")
};
export { providerLabels, displayProviders, ProviderFilters, AuthorType };
